import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { Plus, Edit, Trash2, Church } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';

const MassSchedulesManager = () => {
  const [schedules, setSchedules] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    church: 'karlskrona',
    day_of_week: 'sunday',
    time: '10:00',
    language: '',
    mass_type: 'regular',
    details: '',
    active: true,
  });

  const loadSchedules = async () => {
    const data = await api.getMassSchedules();
    setSchedules(data);
  };

  useEffect(() => {
    loadSchedules();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSchedule) {
        await api.updateMassSchedule(editingSchedule.id, formData);
        toast.success('Mässchema uppdaterat!');
      } else {
        await api.createMassSchedule(formData);
        toast.success('Mässchema tillagt!');
      }
      setIsDialogOpen(false);
      resetForm();
      loadSchedules();
    } catch (error) {
      toast.error('Ett fel uppstod');
    }
  };

  const handleEdit = (schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      church: schedule.church,
      day_of_week: schedule.day_of_week,
      time: schedule.time,
      language: schedule.language,
      mass_type: schedule.mass_type,
      details: schedule.details || '',
      active: schedule.active !== false,
    });
    setIsDialogOpen(true);
  };

  const handleToggleActive = async (schedule) => {
    try {
      await api.updateMassSchedule(schedule.id, { active: !schedule.active });
      toast.success(schedule.active ? 'Mässchema dolt' : 'Mässchema visat');
      loadSchedules();
    } catch (error) {
      toast.error('Ett fel uppstod');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Är du säker?')) {
      await api.deleteMassSchedule(id);
      toast.success('Mässchema raderat!');
      loadSchedules();
    }
  };

  const resetForm = () => {
    setFormData({
      church: 'karlskrona',
      day_of_week: 'sunday',
      time: '10:00',
      language: '',
      mass_type: 'regular',
      details: '',
      active: true,
    });
    setEditingSchedule(null);
  };

  const churches = [
    { id: 'karlskrona', name: 'Karlskrona - Vår Fru av Fatima kyrka' },
    { id: 'karlshamn', name: 'Karlshamn - S:t Bonifatius kyrka' },
    { id: 'olofstrom', name: 'Olofström - S:t Antonius kyrka' },
  ];

  const daysOfWeek = [
    { id: 'monday', name: 'Måndag' },
    { id: 'tuesday', name: 'Tisdag' },
    { id: 'wednesday', name: 'Onsdag' },
    { id: 'thursday', name: 'Torsdag' },
    { id: 'friday', name: 'Fredag' },
    { id: 'saturday', name: 'Lördag' },
    { id: 'sunday', name: 'Söndag' },
  ];

  const massTypes = [
    { id: 'regular', name: 'Vanlig mässa' },
    { id: 'family', name: 'Familjemässa' },
    { id: 'youth', name: 'Ungdomsmässa' },
  ];

  const groupedSchedules = schedules.reduce((acc, schedule) => {
    const church = schedule.church;
    if (!acc[church]) {
      acc[church] = [];
    }
    acc[church].push(schedule);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="mass-schedules-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Mässcheman</h1>
          <p className="text-gray-600">Hantera veckomässor för alla tre kyrkor</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} data-testid="add-schedule-button">
              <Plus className="w-4 h-4 mr-2" />
              Lägg till Mässchema
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingSchedule ? 'Redigera Mässchema' : 'Lägg till Mässchema'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="schedule-form">
              <div>
                <label className="block text-sm font-medium mb-2">Kyrka</label>
                <select
                  value={formData.church}
                  onChange={(e) => setFormData({ ...formData, church: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  data-testid="schedule-church"
                >
                  {churches.map((church) => (
                    <option key={church.id} value={church.id}>
                      {church.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Veckodag</label>
                <select
                  value={formData.day_of_week}
                  onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  data-testid="schedule-day"
                >
                  {daysOfWeek.map((day) => (
                    <option key={day.id} value={day.id}>
                      {day.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Tid</label>
                <Input
                  type="time"
                  required
                  value={formData.time}
                  onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  data-testid="schedule-time"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Språk (valfritt)</label>
                <Input
                  value={formData.language}
                  onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                  placeholder="Svenska, Polska, etc."
                  data-testid="schedule-language"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Mässtyp</label>
                <select
                  value={formData.mass_type}
                  onChange={(e) => setFormData({ ...formData, mass_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  data-testid="schedule-type"
                >
                  {massTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Detaljer (valfritt)</label>
                <Textarea
                  value={formData.details}
                  onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                  placeholder="T.ex. särskilda instruktioner, tema, celebrant..."
                  rows={3}
                  data-testid="schedule-details"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="schedule-active"
                />
                <label htmlFor="active" className="text-sm font-medium">
                  Synlig på webbplatsen
                </label>
              </div>
              <Button type="submit" className="w-full" data-testid="schedule-submit">
                {editingSchedule ? 'Uppdatera' : 'Lägg till'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-8">
        {churches.map((church) => {
          const churchSchedules = groupedSchedules[church.id] || [];
          return (
            <Card key={church.id}>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Church className="w-5 h-5 text-yellow-600" />
                  <span>{church.name}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {churchSchedules.length === 0 ? (
                  <p className="text-gray-500 text-sm">Inga mässcheman tillagda ännu</p>
                ) : (
                  <div className="space-y-3">
                    {churchSchedules
                      .sort((a, b) => {
                        const dayOrder = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 };
                        const dayDiff = dayOrder[a.day_of_week] - dayOrder[b.day_of_week];
                        if (dayDiff !== 0) return dayDiff;
                        return a.time.localeCompare(b.time);
                      })
                      .map((schedule) => (
                        <div
                          key={schedule.id}
                          className={`p-4 rounded-lg border-2 ${
                            schedule.active === false ? 'bg-gray-100 border-gray-300 opacity-60' : 'bg-white border-gray-200'
                          }`}
                          data-testid={`schedule-${schedule.id}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <span className="font-semibold text-gray-900 capitalize">
                                  {daysOfWeek.find((d) => d.id === schedule.day_of_week)?.name}
                                </span>
                                <span className="text-lg font-bold text-gray-700">{schedule.time}</span>
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                                  {schedule.language}
                                </span>
                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                  {massTypes.find((t) => t.id === schedule.mass_type)?.name}
                                </span>
                                {schedule.active === false && (
                                  <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs font-medium">
                                    Dold
                                  </span>
                                )}
                              </div>
                              {schedule.details && (
                                <p className="text-sm text-gray-600 mt-2 pl-1">
                                  <span className="font-medium">Detaljer:</span> {schedule.details}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 ml-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(schedule)}
                                data-testid={`edit-schedule-${schedule.id}`}
                                title="Redigera"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant={schedule.active === false ? "default" : "outline"}
                                size="sm"
                                onClick={() => handleToggleActive(schedule)}
                                data-testid={`toggle-schedule-${schedule.id}`}
                                title={schedule.active === false ? "Visa" : "Dölj"}
                              >
                                {schedule.active === false ? (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                  </svg>
                                )}
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDelete(schedule.id)}
                                data-testid={`delete-schedule-${schedule.id}`}
                                title="Ta bort"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default MassSchedulesManager;
